'use client'

import { useState, useRef, useEffect } from 'react'
import Image from 'next/image'
import LocalizedClientLink from '@modules/common/components/localized-client-link'
import { getProductUrl } from '@lib/util/product'

export interface PickProduct {
  id: string
  title: string | null
  handle: string | null
  thumbnail: string | null
  price: string | null
  originalPrice: string | null
  percentageDiff: string | null
}

interface Props {
  bestsellers: PickProduct[]
  newArrivals: PickProduct[]
  specialOffers: PickProduct[]
}

const TABS = [
  { id: 'bestsellers', label: 'Bestsellers' },
  { id: 'new', label: 'New Arrivals' },
  { id: 'special', label: 'Special Offers' },
] as const

const CARD_GAP = 10
// 48px of the 5th card is always visible as a scroll affordance
const CARD_PEEK = 48
const MIN_CARD_WIDTH = 120
const DEFAULT_CARD_WIDTH = 160

const NavArrow = ({ direction, onClick }: { direction: 'left' | 'right'; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={direction === 'left' ? 'Previous slide' : 'Next slide'}
    style={{
      width: 38,
      height: 38,
      borderRadius: '50%',
      border: '2px solid rgba(20,52,105,0.35)',
      background: 'transparent',
      cursor: 'pointer',
      color: 'rgb(20,52,105)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      padding: 0,
    }}
  >
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      {direction === 'left'
        ? <polyline points="15 18 9 12 15 6" />
        : <polyline points="9 18 15 12 9 6" />}
    </svg>
  </button>
)

function PickCard({ product, cardWidth }: { product: PickProduct; cardWidth: number }) {
  // Image area = card width minus 32px of horizontal padding
  const imageSize = cardWidth - 32

  return (
    <LocalizedClientLink
      href={getProductUrl(product.handle)}
      style={{ width: cardWidth, textDecoration: 'none', display: 'block', flexShrink: 0 }}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: 8,
          padding: 16,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          height: '100%',
        }}
      >
        {/* CUSTOMISE badge — absolute top-left */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            backgroundColor: 'rgb(57,103,171)',
            color: 'white',
            fontSize: 10,
            padding: '2px 4px',
            borderRadius: '4px 0',
            lineHeight: 1.4,
            zIndex: 1,
          }}
        >
          CUSTOMISE
        </div>

        {/* Spacer so image clears the badge */}
        <div style={{ height: 40, flexShrink: 0 }} />

        {/* Product image */}
        <div
          style={{
            width: imageSize,
            height: imageSize,
            position: 'relative',
            marginBottom: 8,
            flexShrink: 0,
          }}
        >
          {product.thumbnail ? (
            <Image
              src={product.thumbnail}
              alt={product.title ?? ''}
              fill
              className="object-contain"
              sizes={`${cardWidth}px`}
            />
          ) : (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgb(200,200,200)', fontSize: 11 }}>
              No image
            </div>
          )}
        </div>

        {/* Title — 2-line clamp */}
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <p
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: 'rgb(20,52,105)',
              lineHeight: '20px',
              margin: '0 0 6px',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            } as React.CSSProperties}
          >
            {product.title}
          </p>

          {/* Price */}
          <div style={{ marginTop: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: 'rgb(0,0,0)', lineHeight: 1 }}>
                {product.price}
              </span>
              {product.originalPrice && (
                <span style={{ fontSize: 13, fontWeight: 400, color: 'rgb(125,125,125)', textDecoration: 'line-through', lineHeight: 1 }}>
                  {product.originalPrice}
                </span>
              )}
            </div>
            {product.percentageDiff && (
              <div style={{ fontSize: 12, fontWeight: 600, color: 'rgb(0,140,70)', marginTop: 2 }}>
                {product.percentageDiff}% OFF
              </div>
            )}
          </div>
        </div>
      </div>
    </LocalizedClientLink>
  )
}

export default function EPLPicksTabs({ bestsellers, newArrivals, specialOffers }: Props) {
  const [active, setActive] = useState<'bestsellers' | 'new' | 'special'>('bestsellers')
  const scrollRef = useRef<HTMLDivElement>(null)
  const sectionRef = useRef<HTMLDivElement>(null)
  const [cardWidth, setCardWidth] = useState(DEFAULT_CARD_WIDTH)

  // Measure section width → calculate exact card width for 4 full + partial 5th
  useEffect(() => {
    const measure = () => {
      if (!sectionRef.current) return
      // Section has 12px left + 12px right padding = 24px total horizontal padding
      const available = sectionRef.current.offsetWidth - 24
      // 4 full cards + 3 gaps between them + CARD_PEEK of the 5th
      const w = Math.floor((available - CARD_GAP * 3 - CARD_PEEK) / 4)
      setCardWidth(Math.max(MIN_CARD_WIDTH, w))
    }
    measure()
    const ro = new ResizeObserver(measure)
    if (sectionRef.current) ro.observe(sectionRef.current)
    return () => ro.disconnect()
  }, [])

  const products = active === 'bestsellers' ? bestsellers : active === 'new' ? newArrivals : specialOffers

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return
    const amount = (cardWidth + CARD_GAP) * 2
    scrollRef.current.scrollBy({ left: direction === 'right' ? amount : -amount, behavior: 'smooth' })
  }

  const switchTab = (id: 'bestsellers' | 'new' | 'special') => {
    setActive(id)
    scrollRef.current?.scrollTo({ left: 0, behavior: 'instant' })
  }

  return (
    <div
      ref={sectionRef}
      style={{
        backgroundColor: 'rgb(227,237,251)',
        padding: '18px 12px 14px',
        margin: '26px 0',
        borderRadius: 10,
      }}
    >
      {/* Tab bar + nav arrows */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 6, flex: 1 }}>
          {TABS.map((tab) => {
            const isActive = active === tab.id
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => switchTab(tab.id)}
                style={{
                  padding: '0 14px',
                  height: 28,
                  borderRadius: 16,
                  fontSize: 12.5,
                  fontWeight: isActive ? 600 : 500,
                  border: '1px solid',
                  cursor: 'pointer',
                  backgroundColor: isActive ? 'rgb(20,52,105)' : 'white',
                  color: isActive ? 'white' : 'rgb(68,68,68)',
                  borderColor: isActive ? 'rgb(20,52,105)' : 'rgb(200,208,224)',
                  whiteSpace: 'nowrap',
                  lineHeight: 1,
                  transition: 'background-color 0.15s, color 0.15s, border-color 0.15s',
                }}
              >
                {tab.label}
              </button>
            )
          })}
        </div>

        {/* Nav arrows */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          <NavArrow direction="left" onClick={() => scroll('left')} />
          <NavArrow direction="right" onClick={() => scroll('right')} />
        </div>
      </div>

      {/* Carousel */}
      <div
        ref={scrollRef}
        style={{ overflowX: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' } as React.CSSProperties}
      >
        <div style={{ display: 'flex', gap: CARD_GAP, minWidth: 'max-content' }}>
          {products.map((p) => (
            <PickCard key={p.id} product={p} cardWidth={cardWidth} />
          ))}
        </div>
      </div>
    </div>
  )
}
